import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { getClub, getClubMembers, updateClub, deleteClub, removeClubMember, updateClubMember, leaveClub, getClubInvitations, cancelInvitation } from '../services/clubs';
import { ClubWithDetails, ClubMemberWithDetails } from '../types';
import { PlayerInvitationDetails } from '../services/clubs';
import Loading from '../components/Loading';
import PlayerInviteModal from '../components/PlayerInviteModal';
import ConfirmModal from '../components/ConfirmModal';
// Tournament management moved to separate page
import TournamentRegistration from '../components/TournamentRegistration';
import { TableManagement } from '../components/TableManagement';
import './ClubManagement.css';

const ClubManagement: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { player } = useAuth();
  const [club, setClub] = useState<ClubWithDetails | null>(null);
  const [members, setMembers] = useState<ClubMemberWithDetails[]>([]);
  const [invitations, setInvitations] = useState<PlayerInvitationDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingClub, setEditingClub] = useState(false);
  const [clubFormData, setClubFormData] = useState({
    name: '',
    description: '',
  });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showLeaveClubModal, setShowLeaveClubModal] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState<'owner' | 'admin' | 'organizer' | 'member' | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'tournaments' | 'members' | 'tables'>('overview');
  const [tournamentRefreshKey, setTournamentRefreshKey] = useState(0);

  // Function to trigger tournament data refresh
  const handleTournamentDataRefresh = useCallback(() => {
    setTournamentRefreshKey(prev => prev + 1);
  }, []);

  const loadClubData = useCallback(async (clubId: number) => {
    try {
      setLoading(true);
      setError(null);
      
      const [clubData, membersData, invitationsData] = await Promise.all([
        getClub(clubId),
        getClubMembers(clubId),
        getClubInvitations(clubId)
      ]);
      
      setClub(clubData);
      setMembers(membersData);
      setInvitations(invitationsData);
      setClubFormData({
        name: clubData.name,
        description: clubData.description || '',
      });

      // Check if current user is owner or admin
      if (player) {
        const currentUserMembership = membersData.find(m => m.player_id === player.id);
        if (currentUserMembership) {
          const role = currentUserMembership.role as 'owner' | 'admin' | 'organizer' | 'member';
          setCurrentUserRole(role);
          setIsOwner(role === 'owner');
          setIsAdmin(role === 'admin' || role === 'owner');
          setIsOrganizer(role === 'organizer' || role === 'admin' || role === 'owner');
        } else {
          setCurrentUserRole(null);
          setIsOwner(false);
          setIsAdmin(false);
          setIsOrganizer(false);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load club data');
    } finally {
      setLoading(false);
    }
  }, [player]);

  useEffect(() => {
    if (id) {
      loadClubData(parseInt(id));
    }
  }, [id, loadClubData]);

  const handleClubUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!club) return;

    try {
      setLoading(true);
      await updateClub(club.id, {
        name: clubFormData.name.trim(),
        description: clubFormData.description.trim() || undefined,
      });
      
      await loadClubData(club.id);
      setEditingClub(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update club');
    } finally {
      setLoading(false);
    }
  };

  const handleClubDelete = async () => {
    if (!club) return;
    
    if (!window.confirm(t('clubs.deleteConfirm'))) {
      return;
    }

    try {
      setLoading(true);
      await deleteClub(club.id);
      navigate('/clubs');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete club');
      setLoading(false);
    }
  };

  const handleLeaveClub = async () => {
    if (!club) return;

    try {
      setLoading(true);
      await leaveClub(club.id);
      setShowLeaveClubModal(false);
      navigate('/clubs');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to leave club');
      setLoading(false);
    }
  };

  const handleCancelInvitation = async (invitationId: number, playerName: string) => {
    if (!window.confirm(t('clubs.cancelInvitationConfirm', { name: playerName }))) {
      return;
    }

    try {
      await cancelInvitation(invitationId);
      await loadClubData(club!.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel invitation');
    }
  };

  const handleMemberRoleChange = async (membershipId: number, newRole: string) => {
    try {
      await updateClubMember(membershipId, { role: newRole as any });
      await loadClubData(club!.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update member role');
    }
  };

  const handleMemberRemove = async (membershipId: number, memberName: string) => {
    if (!window.confirm(t('clubs.removeMemberConfirm', { name: memberName }))) {
      return;
    }

    try {
      await removeClubMember(membershipId);
      await loadClubData(club!.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'owner': return 'role-badge owner';
      case 'admin': return 'role-badge admin';
      case 'member': return 'role-badge member';
      default: return 'role-badge member';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return t('clubs.roles.owner');
      case 'admin': return t('clubs.roles.admin');
      case 'member': return t('clubs.roles.member');
      default: return t('clubs.roles.member');
    }
  };

  if (loading) return <Loading />;

  if (!club) {
    return (
      <div className="club-management-container">
        <div className="error-message">
          <p>{t('clubs.clubNotFound')}</p>
          <button onClick={() => navigate('/clubs')} className="btn btn-primary">
            {t('clubs.backToClubs')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="club-management-container">
      <div className="club-management-header">
        <div className="header-left">
          <button onClick={() => navigate('/clubs')} className="btn btn-secondary">
            ← {t('clubs.backToClubs')}
          </button>
          <h1>{club.name}</h1>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      {/* Club Tabs */}
      <div className="club-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          {t('clubs.overview')}
        </button>
        <button 
          className={`tab ${activeTab === 'tournaments' ? 'active' : ''}`}
          onClick={() => setActiveTab('tournaments')}
        >
          {t('clubs.tournaments')}
        </button>
        <button 
          className={`tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          👥 {t('clubs.members')}
        </button>
        {(isOwner || isAdmin || isOrganizer) && (
          <button 
            className={`tab ${activeTab === 'tables' ? 'active' : ''}`}
            onClick={() => setActiveTab('tables')}
          >
            📱 {t('tableDevices.title')}
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <>
          {/* Club Information */}
      <div className="club-info-section">
        <h2>{t('clubs.clubInformation')}</h2>
        {(isOwner || isAdmin) && editingClub ? (
          <form onSubmit={handleClubUpdate} className="club-edit-form">
            <div className="form-group">
              <label htmlFor="clubName" className="form-label">
                {t('clubs.clubName')}
              </label>
              <input
                type="text"
                id="clubName"
                value={clubFormData.name}
                onChange={(e) => setClubFormData(prev => ({ ...prev, name: e.target.value }))}
                className="form-input"
                required
                disabled={loading}
              />
            </div>
            <div className="form-group">
              <label htmlFor="clubDescription" className="form-label">
                {t('clubs.description')}
              </label>
              <textarea
                id="clubDescription"
                value={clubFormData.description}
                onChange={(e) => setClubFormData(prev => ({ ...prev, description: e.target.value }))}
                className="form-textarea"
                rows={3}
                disabled={loading}
              />
            </div>
            <div className="form-actions">
              <button
                type="button"
                onClick={() => setEditingClub(false)}
                className="btn btn-secondary"
                disabled={loading}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {t('clubs.saveChanges')}
              </button>
            </div>
          </form>
        ) : (
          <div className="club-details-compact">
            <div className="club-meta-row">
              {club.code && (
                <div className="club-meta-item">
                  <span className="meta-label">{t('clubs.clubCode')}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(club.code || '');
                      alert(t('clubs.codeCopied'));
                    }}
                    className="club-code-badge clickable"
                    title={t('clubs.copyCode')}
                  >
                    {club.code}
                  </button>
                </div>
              )}
              <div className="club-meta-item">
                <span className="meta-label">{t('clubs.created')}</span>
                <span className="meta-value">{new Date(club.created_at).toLocaleDateString()}</span>
              </div>
              <div className="club-meta-item">
                <span className="meta-label">{t('clubs.members')}</span>
                <span className="meta-value">{club.member_count}</span>
              </div>
            </div>
            {club.description && (
              <div className="club-description-row">
                <span className="meta-label">{t('clubs.description')}</span>
                <p className="club-description-text">{club.description}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Tournaments & Events Section - Visible to organizers, admins, and owners */}
      {isOrganizer && club && (
        <div className="events-section">
          <div className="events-header">
            <h2>{t('clubs.tournamentsAndEvents')}</h2>
            <p className="events-description">{t('clubs.eventsDescription')}</p>
            <div className="events-actions">
              <button 
                onClick={() => navigate(`/clubs/${club.id}/tournaments`)}
                className="btn btn-primary"
              >
                🏆 {t('clubs.manageTournaments')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Admin Actions - Only visible in Overview Tab */}
      {(isOwner || isAdmin || currentUserRole === 'member') && (
        <div className="admin-actions-section">
          <h3>{t('clubs.clubActions')}</h3>
          <div className="admin-actions-buttons">
            {(isOwner || isAdmin) && (
              <button
                onClick={() => setEditingClub(!editingClub)}
                className="btn btn-outline"
              >
                ✏️ {editingClub ? t('common.cancel') : t('clubs.editClub')}
              </button>
            )}
            {isOwner && (
              <button
                onClick={handleClubDelete}
                className="btn btn-danger"
              >
                🗑️ {t('clubs.deleteClub')}
              </button>
            )}
            {currentUserRole === 'member' && (
              <button
                onClick={() => setShowLeaveClubModal(true)}
                className="btn btn-warning"
              >
                🚪 {t('clubs.leaveClub')}
              </button>
            )}
          </div>
        </div>
      )}
        </>
      )}

      {activeTab === 'tournaments' && (
        <div className="tournaments-section">
          <TournamentRegistration key={tournamentRefreshKey} clubId={club.id} />
        </div>
      )}

      {/* Members Tab - Visible to all members, management functions for owners and admins */}
      {activeTab === 'members' && (
        <div className="members-tab-section">
          {/* Members Management */}
          {members.length > 0 && (
            <div className="members-section">
              <div className="members-header">
                <h2>{t('clubs.members')} ({members.length})</h2>
                {(isOwner || isAdmin) && (
                  <button 
                    onClick={() => setShowInviteModal(true)}
                    className="btn btn-primary"
                  >
                    {t('clubs.invitePlayer')}
                  </button>
                )}
              </div>
              
              <div className="members-list">
                {members.map(member => (
                  <div key={member.id} className="member-card">
                    <div className="member-info">
                      <h4 className="member-name">{member.player_name}</h4>
                      <p className="member-email">{member.player_email}</p>
                      <div className="member-meta">
                        <span className={getRoleBadgeClass(member.role)}>
                          {getRoleLabel(member.role)}
                        </span>
                        <span className="joined-date">
                          {t('clubs.joined')}: {new Date(member.joined_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="member-actions">
                      <select
                        value={member.role}
                        onChange={(e) => handleMemberRoleChange(member.id, e.target.value)}
                        className="role-select"
                        disabled={member.role === 'owner'}
                      >
                        <option value="member">{t('clubs.roles.member')}</option>
                        <option value="organizer">{t('clubs.roles.organizer')}</option>
                        <option value="admin">{t('clubs.roles.admin')}</option>
                      </select>
                      <button
                        onClick={() => handleMemberRemove(member.id, member.player_name)}
                        className="btn btn-danger btn-small"
                        disabled={member.role === 'owner'}
                      >
                        {t('clubs.removeMember')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pending Invitations - Only show pending invitations */}
          {(isOwner || isAdmin) && (
            <div className="invitations-section">
              <div className="invitations-header">
                <h2>{t('clubs.pendingInvitations')} ({invitations.filter(inv => inv.status === 'pending').length})</h2>
              </div>
              <div className="invitations-list">
                {invitations.filter(inv => inv.status === 'pending').length === 0 ? (
                  <p className="no-invitations">{t('clubs.noPendingInvitations')}</p>
                ) : (
                  invitations
                    .filter(inv => inv.status === 'pending')
                    .map((invitation) => (
                      <div key={invitation.id} className="invitation-card">
                        <div className="invitation-info">
                          <h4 className="invitation-player-name">{invitation.invited_player_name}</h4>
                          <p className="invitation-email">{invitation.invited_player_email}</p>
                          <div className="invitation-meta">
                            <span className={`role-badge role-${invitation.role}`}>
                              {t(`clubs.roles.${invitation.role}`)}
                            </span>
                            <span className="invitation-date">
                              {t('clubs.invitedOn')}: {new Date(invitation.created_at).toLocaleDateString()}
                            </span>
                            <span className="invitation-expires">
                              {t('clubs.expiresOn')}: {new Date(invitation.expires_at).toLocaleDateString()}
                            </span>
                          </div>
                          {invitation.message && (
                            <p className="invitation-message">{invitation.message}</p>
                          )}
                        </div>
                        <div className="invitation-actions">
                          <button
                            onClick={() => handleCancelInvitation(invitation.id, invitation.invited_player_name)}
                            className="btn btn-danger btn-small"
                          >
                            {t('clubs.cancelInvitation')}
                          </button>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Player Invitation Modal */}
      {club && (
        <PlayerInviteModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          clubId={club.id}
          onPlayerInvited={() => {
            loadClubData(club.id);
            setShowInviteModal(false);
          }}
        />
      )}

      {/* Tables Tab */}
      {activeTab === 'tables' && (
        <TableManagement clubId={club.id} />
      )}

      {/* Leave Club Confirmation Modal */}
      <ConfirmModal
        isOpen={showLeaveClubModal}
        onClose={() => setShowLeaveClubModal(false)}
        onConfirm={handleLeaveClub}
        title={t('clubs.leaveClub')}
        message={t('clubs.leaveClubConfirm')}
        confirmText={t('clubs.leaveClub')}
        cancelText={t('common.cancel')}
        confirmButtonClass="btn-warning"
        loading={loading}
      />
    </div>
  );
};

export default ClubManagement;
